import * as Service from 'types/service';
import {
  EventAction,
  EventAttributesActionLocation,
} from 'utils/analytics/amplitudeEventConstants';
import { FC } from 'react';
import { PartyDragSource } from 'components/service/party/PartyDrag';
import { SquareCoordinates } from 'actions/CalendarActions';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { flexSelector } from 'selectors/featuresSelector';
import { getSelectedPartyId } from 'selectors/service/serviceUiSelector';
import { isDiningVariety } from '@tock/shared/utils/offerings';
import { useActions } from '@tock/shared/utils/connect';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useIsCompactView } from 'hooks/useIsCompactView';
import { useSelector } from 'react-redux';

import PartyRedesignCardView, {
  PartyRedesignCardViewProps,
} from 'components/service/party/partyRedesignCard/PartyRedesignCardView';
import useDraggableParty from 'components/service/party/useDraggableParty';

interface Props {
  dragSource: PartyDragSource;
  party: Service.Party;
  showGuestListTags?: boolean;
  isAssignable?: boolean;
  isEligibleForHoldCharge?: boolean;
  isReadOnly: boolean;
  showUnassignedWarning?: boolean;
  onSelectParty(party: Service.Party): void;
  onSelectPartyForPreassign?(party: Service.Party): void;
  onTogglePartyPopover(
    party?: Service.Party,
    coordinates?: SquareCoordinates,
    anchorEle?: HTMLDivElement
  ): void;
  isInTimeline: boolean;
}

/**
 * This is a variant of PartyRedesignCard that
 * 1) does not use HammerJS, which was not working as-intended on touch devices to begin with
 *    (and appears to have been in a broken state for a while), and
 * 2) makes no use of useRef as at, in particular not applied to HTML Elements in the render.
 *    There is some suggestion in online bugreports that the current dashboard version of React,
 *    17.0.2, will leak memory if a component useRef() points at an HTML element
 *    for a component that gets cyclically mounted and unmounted, which does
 *    happen for PartyRedesignCard due to the way it interacts with react-virtualized
 *    https://github.com/facebook/react/issues/27892
 */
const PartyRedesignCardWithMemoryMitigation: FC<Props> = ({
  dragSource,
  party,
  isAssignable = false,
  isInTimeline,
  isReadOnly,
  showUnassignedWarning = false,
  onSelectParty,
  onSelectPartyForPreassign,
  onTogglePartyPopover,
}) => {
  const { drag, isDragging } = useDraggableParty(party, dragSource);
  const isSelected = useSelector(getSelectedPartyId) === party.id;
  const isCompactView = useIsCompactView();
  const isFlexBusiness = useSelector(flexSelector);
  const isPartyStatusRefreshEnabled = useFeatureFlag('PARTY_STATUS_REFRESH');
  const { actions } = useActions();

  /**
   * Chooses between party selection or assignment actions when clicking on the
   * party status indicator.  The assignment action is only available
   * for expected+seated parties in service.
   */
  const handleStatusIndicatorClick = () => {
    if (
      isInTimeline ||
      !onSelectPartyForPreassign ||
      party.state === 'NO_SHOW' ||
      party.state === 'CANCELED' ||
      party.state === 'LEFT'
    ) {
      onSelectParty(party);
    } else {
      onSelectPartyForPreassign(party);
    }
  };

  /**
   * Shows the party popover menu
   */
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const { currentTarget } = e;
    const { x, y } = currentTarget.getBoundingClientRect();
    const coordinates = {
      openPositionX: x,
      openPositionY: y,
    };
    amplitudeSendEvent(EventAction.PARTY_STATUS_QUICK_ACTION_OPENED, {
      actionLocation: EventAttributesActionLocation.PARTY_POPOVER,
    });
    onTogglePartyPopover(party, coordinates, currentTarget as HTMLDivElement);
  };

  // The logic on how to treat an unassigned table is now entirely localized to this component; PartyRedesignStatusIndicator
  // just does dumb display of this detail. (Once it has determined that a table *is* indeed unassigned, it still has the
  // job of making that determination.
  let unassignedTreatment: PartyRedesignCardViewProps['unassignedTreatment'];
  const isBooking = party.type === 'Booking';
  const ticketIsDiningVariety = isDiningVariety(party.purchase?.ticketType?.variety);

  if (showUnassignedWarning && isBooking && ticketIsDiningVariety) {
    unassignedTreatment = isFlexBusiness || !isInTimeline ? 'warning' : 'warningNoGrabCursor';
  } else if (isAssignable && (isBooking || !isInTimeline)) {
    // TODO: Review the logic above when M3 happens. Once dragging on timeline for walkins is enabled that logic will change.
    unassignedTreatment = 'dragIcon';
  } else {
    unassignedTreatment = 'empty';
  }
  const assignedTreatment = isFlexBusiness || !isInTimeline ? 'normal' : 'noGrabCursor';
  const handleSave = (party: Service.Party, state: string, secondaryState: string | null) => {
    actions.elmParties.updatePartyState(
      party,
      state,
      secondaryState,
      [],
      EventAttributesActionLocation.PARTY_POPOVER
    );
  };

  return (
    <PartyRedesignCardView
      party={party}
      isSelected={isSelected}
      isDragging={isDragging}
      isCompactView={isCompactView}
      dragRef={!isReadOnly ? drag : undefined}
      onContextMenu={handleContextMenu}
      onBodyClick={() => onSelectParty(party)}
      onStatusIndicatorClick={handleStatusIndicatorClick}
      onUpdatePartyState={handleSave}
      unassignedTreatment={unassignedTreatment}
      assignedTreatment={assignedTreatment}
      isPartyStatusRefreshEnabled={isPartyStatusRefreshEnabled}
    />
  );
};

export default PartyRedesignCardWithMemoryMitigation;
